import utils from 'utils/utils';
import { Model } from 'utils/model';
import image from 'utils/image';
import { objectToString } from 'utils/string';
import ServerTime from 'utils/server-time';

import config from 'player/config';
import Domain from 'player/model/domain';
import Token from 'player/model/token';
import TokenV2 from 'player/model/tokenv2';
import { TokenStore } from 'player/model/token-store';
import { AssetModel } from 'player/model/asset';
import { StreamModel } from 'player/model/stream';

import locale from 'player/model/locale';

import appnexus from './ads/appnexus';
import addPlaylistFactory from './config/playlist';
import { addSharing } from './config/sharing';
import setPlaybackOptions from './config/playback';
import addYoubora from './config/jw-youbora';
import setKey from './config/keys';
import setCaptions from './config/captions';
import { chromecastApps, fallbackChromecastApp } from './config/chromecast';
import { applyTtsOption } from './skin/tts-skin';
import { shouldUseAudioSkin } from './skin/audioSkinResolver';

import * as STATUS_CODES from './config/status-codes';
import { TOKEN_FETCH_ERROR } from './config/status-codes';
import { youboraSdk } from './youbora';
import type { Config as PlainConfig } from '../../../../types/index';

function getAkamaiReferenceId(htmlError?: string): string | undefined {
    return htmlError
        ?.split('Reference&#32;&#35;')
        ?.at(-1)
        ?.split('\n')
        ?.at(0)
        ?.replace(/&#46;/g, '.');
}

/**
 * Config instance for player
 * Contains SVP Player options
 * Parses config
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class ConfigModel extends Model<
    PlainConfig & {
        env: string;
        stream: StreamModel | null;
        about: { text: string; link: string };
        forceImaClientLoading: boolean;
    }
> {
    /**
     * JW Player Config
     */
    jwDefaults: {
        base: string;
        key: string;
        preload: string;
        horizontalVolumeSlider: boolean;
        doNotSaveCookies: boolean;
    };
    stream: StreamModel = null!;
    token: null | TokenV2 | Token;
    rawOptions: PlainConfig = null!;
    tokenStore = new TokenStore();

    constructor() {
        super({
            // DOM node
            node: null!,
            // API vendor
            vendor: null!,
            // player environment
            env: 'production',
            // stream object (extracted from asset)
            stream: null,
            // if not provided width will be read from node element
            width: '100%',
            // if not provided height will be read from node element
            height: '100%',
            // start playing from chapter time
            chapter: null!,
            // start stream automatically on capable devices (does not work on iPhone and iPad, Android)
            autoplay: false,
            // player poster (image), mixed string (http src)|true|false
            poster: true as unknown as string,
            // display title on poster or change to provided string (default false)
            title: null!,
            // play ahead time, format XXhYYmZZs, for example: 02h09m10s
            time: null!,
            // repeat mode
            repeat: false,
            // minimum dvr window
            minDvrWindow: null!,
            // url to css file with skin
            skin: config.skins.default,
            // right click defualt text and link
            about: {
                text: 'Stream',
                link: '',
            },
            /**
             * Token function for secured streams
             * @type {Token|null}
             */
            token: null!,
            // disable default countdown plugin
            disableCountdownPlugin: false,
        });

        this.jwDefaults = {
            base: config.cdn.player,
            key: config.player.keys.default,
            preload: 'auto',
            horizontalVolumeSlider: true,
            doNotSaveCookies: true,
        };

        this.token = null;
    }

    /**
     * Calculates bitrate value based on given limit
     *
     * maxBitrate is increased by 15% due to VBR encoding
     */
    getMaxBitrate() {
        return this.get('maxBitrate')! * 1.15;
    }

    setStream(stream: StreamModel) {
        this.stream = stream;
        this.token = this.tokenStore.get({
            stream,
            useV2: !!this.get('tokenV2Url'),
        });
    }

    /**
     * Get config passed to SVP Player constructor
     */
    getRaw(): PlainConfig {
        return { ...this.rawOptions };
    }

    isPreviewMode(): boolean {
        const settings = this.getSettings();
        return settings.preview || false;
    }

    /**
     * Dump Javascript config to a string
     */
    dump(): string {
        return objectToString(this.getRaw());
    }

    /**
     * TODO add tests
     */
    getPoster(
        stream: StreamModel,
        playerContainer?: HTMLElement,
    ): string | null {
        // disable poster in TTS mode
        if (this.shouldApplyTextToSpeechSkin(stream)) {
            return null;
        }

        const container =
            playerContainer || document.getElementById(this.get('node'));

        const width = this.get('width');
        const height = this.get('height');

        const configPoster = this.get('poster');
        if (typeof configPoster === 'string') {
            return configPoster;
        }

        if (container && stream) {
            return image.getImageSrc(
                stream.getPoster(),
                'poster',
                // use width and height if it's set as a number
                utils.isNumber(width) ? width : container.clientWidth,
                utils.isNumber(height) ? height : container.clientHeight,
            );
        }

        return null;
    }

    shouldApplyTextToSpeechSkin(stream: StreamModel): boolean {
        const ttsSkinOption = this.get('ttsSkin');
        if (typeof ttsSkinOption === 'boolean') {
            return ttsSkinOption;
        }

        const currentStream = stream || this.stream;
        return Boolean(currentStream) && currentStream.hasTextSpeech();
    }

    getVideoPreviewMetadataKey(): string | undefined {
        const { videoPreview } = this.getRaw();
        const isCustomMetadataKey = typeof videoPreview === 'string';

        const allPreviewsKeys = Object.keys(this.stream.getAllPreviewsUrls());

        if (!(videoPreview === true || isCustomMetadataKey)) {
            return undefined;
        }

        if (isCustomMetadataKey) {
            return videoPreview;
        }

        if (
            this.stream.isLive() &&
            allPreviewsKeys.includes(config.liveVideoPreview)
        ) {
            return config.liveVideoPreview;
        }

        return (
            config.videoPreview[
                this.get('vendor') as keyof typeof config.videoPreview
            ] || config.videoPreview.default
        );
    }

    getVideoPreviewUrl(
        stream: StreamModel,
        playerContainer?: HTMLElement,
    ): string | undefined {
        const container =
            playerContainer || document.getElementById(this.get('node'));
        if (!container || !stream || stream.isAudio()) {
            return undefined;
        }

        const metadataKey = this.getVideoPreviewMetadataKey();

        return metadataKey ? stream.getVideoPreviewUrl(metadataKey) : undefined;
    }

    getTitle(stream: StreamModel): string {
        const title = this.get('title');
        const isAudioWithoutCustomTitle =
            stream.isAudio() && typeof title !== 'string';

        if (title === true || isAudioWithoutCustomTitle) {
            return stream.get('title');
        }

        return title as string;
    }

    getDescription(stream: StreamModel): string | undefined {
        if (stream.isAudio()) {
            return stream.getCategory().getTitle();
        }

        return undefined;
    }

    getLiveSyncDuration(stream: StreamModel) {
        const chunkDuration = stream.getChunkDuration();
        if (chunkDuration) {
            // https://github.com/video-dev/hls.js/blob/master/docs/API.md#livesyncduration
            return chunkDuration * 2 + 1;
        }
        return undefined;
    }

    /**
     * Retrieve token required for secure streams
     * Token expiry has to match API
     */
    getToken(): Promise<string | null> {
        if (this.stream.isSecure() && this.token) {
            return this.token.fetch(
                this.stream,
                this.get('token'),
                this.getMaxBitrate(),
            );
        }

        return Promise.resolve(null);
    }

    /**
     * Token is required only for secure streams so for all other types it's valid
     */
    hasValidToken(): boolean {
        return !this.stream.isSecure() || this.token!.isValid();
    }

    async getStreamUrl(type: 'hls' | 'mp4'): Promise<string | null> {
        // stream not set yet
        if (!this.stream) {
            console.error('SVP SDK: getStreamUrl() called before stream set');
            return null;
        }

        if (this.stream.isMock()) {
            return this.stream.getUrl('hls') as string;
        }

        const maxBitrate = this.getMaxBitrate();

        const appendBitrate = (url: string) => {
            if (!maxBitrate) {
                return url;
            }
            return `${url}${url.includes('?') ? '&' : '?'}b=0-${maxBitrate}`;
        };

        const plainStreamUrl = this.stream.getUrl(type) as string;
        if (type === 'hls' && this.stream.isSecure() && this.token) {
            const url = await this.token.getSignedUrl(
                this.stream,
                this.get('token')!,
                maxBitrate,
            );
            if (url) {
                return appendBitrate(url);
            }

            // Stream Admin: url already contains token
            if (
                plainStreamUrl.includes('hdnea=') ||
                plainStreamUrl.includes('hdnts=')
            ) {
                return appendBitrate(plainStreamUrl);
            }

            throw TOKEN_FETCH_ERROR;
        }

        return appendBitrate(plainStreamUrl);
    }

    async getDrmToken(stream: StreamModel): Promise<string | null> {
        if (!stream) {
            console.error('SVP SDK: getDrmToken() called before stream set');
            return null;
        }

        if (stream.isMock() || !stream.isDrmProtected()) {
            return null;
        }

        const externalDrmToken = this.get('drmToken');
        if (externalDrmToken) {
            if (typeof externalDrmToken === 'function') {
                return externalDrmToken({
                    id: stream.getId(),
                    vendor: stream.getVendor(),
                });
            }
            return externalDrmToken;
        }

        const token = this.tokenStore.get({
            stream,
            useV2: !!this.get('tokenV2Url'),
        });
        return token.getDrmToken(stream);
    }

    /**
     * Check if user is eligible to play stream in his geolocation
     */
    isStreamPlayable(): Promise<void | number> {
        const { stream } = this;
        const settings = this.getSettings();

        return new Promise((resolve, reject) => {
            if (settings.preview === true) {
                resolve(STATUS_CODES.ACTIVE_PREVIEW);
            } else if (!ServerTime.fetched) {
                reject({ code: STATUS_CODES.NETWORK_ERROR });
            } else if (stream.isActive()) {
                if (stream.isGeoblocked() && !stream.isFuture()) {
                    this.getStreamUrl('hls')
                        .then(async (streamUrl) => {
                            const response = await fetch(streamUrl!);

                            if (response.ok) return resolve();

                            const htmlError = await response.text?.();
                            const referenceId = getAkamaiReferenceId(htmlError);

                            if (response.status === 403) {
                                return reject({
                                    code: STATUS_CODES.NOT_ACTIVE_GEOBLOCKED,
                                    referenceId,
                                });
                            }
                            if (response.status === 401) {
                                return reject({
                                    code: STATUS_CODES.NOT_ACTIVE_TOKEN,
                                    referenceId,
                                });
                            }
                            if (response.status === 404) {
                                return reject({
                                    code: STATUS_CODES.NOT_FOUND,
                                    referenceId,
                                });
                            }
                            return reject({
                                code: STATUS_CODES.NETWORK_ERROR,
                                referenceId,
                            });
                        })
                        .catch(() => {
                            reject({ code: STATUS_CODES.NETWORK_ERROR });
                        });
                } else {
                    resolve(STATUS_CODES.ACTIVE);
                }
            } else if (stream.isPast()) {
                reject({ code: STATUS_CODES.NOT_ACTIVE_PAST });
            } else {
                reject({ code: STATUS_CODES.NOT_ACTIVE });
            }
        });
    }

    getPlaylistItems(stream: StreamModel): StreamModel[] {
        if (!stream) {
            return [];
        }
        const vendor = this.get('vendor');
        const playlistStreams = stream
            .get('playlist')
            .map((data) => {
                const asset = new AssetModel(data);
                asset.set('vendor', vendor);
                return asset;
            })
            .map((asset) => new StreamModel(asset.attributes));
        return [stream].concat(playlistStreams);
    }

    getChromecastAppId() {
        const vendorEntry = Object.entries(chromecastApps).find((entry) =>
            entry[1].includes(this.stream.getVendor()),
        );
        return (vendorEntry && vendorEntry[0]) || fallbackChromecastApp;
    }

    getJwConfig(stream: StreamModel) {
        const settings = this.getSettings();

        return (
            Promise.resolve({
                width: this.get('width'),
                height: this.get('height'),
                abouttext: this.get('about').text,
                aboutlink: this.get('about').link,
                floating: this.get('floating'),
                sharing: { ...config.sharing.global },
                localization: locale.translate('player', true),
                plugins: {},
                cast: {
                    customAppId: this.getChromecastAppId(),
                },
                playlist: [],
                ...(this.getLiveSyncDuration(stream)
                    ? { liveSyncDuration: this.getLiveSyncDuration(stream) }
                    : {}),
            } as jwplayer.SetupConfig)
                .then((jwConfig): Promise<jwplayer.SetupConfig> => {
                    const playlistItems = this.getPlaylistItems(stream);
                    const addPlaylistItemsPromise = playlistItems.reduce(
                        (promise, currentStream) => {
                            const streamUrlPromise = promise.then(() => {
                                // change to the current stream object to prepare a valid stream url
                                this.setStream(currentStream);
                                return this.getStreamUrl('hls');
                            });

                            return streamUrlPromise.then((streamUrl) => {
                                const playlistSettings =
                                    this.getPlaylistSettings();
                                const playlistOptions = {
                                    poster: this.getPoster(currentStream),
                                    preview:
                                        this.getVideoPreviewUrl(currentStream),
                                    title: this.getTitle(currentStream),
                                    description:
                                        this.getDescription(currentStream),
                                    locale: locale,
                                    minDvrWindow: this.get('minDvrWindow'),
                                };

                                const applyPlaylistAdding = addPlaylistFactory(
                                    streamUrl!,
                                    currentStream,
                                    playlistOptions,
                                    playlistSettings,
                                    {
                                        forceImaClientLoading: this.get(
                                            'forceImaClientLoading',
                                        ),
                                        shouldUseAudioSkin:
                                            shouldUseAudioSkin(this),
                                        getDrmToken: () =>
                                            this.getDrmToken(currentStream),
                                    },
                                );

                                return applyPlaylistAdding(jwConfig);
                            });
                        },
                        Promise.resolve() as unknown as Promise<jwplayer.SetupConfig>,
                    );

                    return addPlaylistItemsPromise.then(() => {
                        // revert to the origin stream object
                        this.setStream(stream);
                        return jwConfig as jwplayer.SetupConfig;
                    });
                })
                .then(
                    addSharing({
                        options: this.get('sharing'),
                        stream,
                    }),
                )
                .then((jwConfig) => applyTtsOption(jwConfig, stream, this))
                .then(setCaptions(this.get('captions')))
                // setPlaybackOptions has to be invoked in the closure to inject the current values of mute, skin, etc.
                // otherwise it will get values when getJwConfig was called
                .then((jwConfig) =>
                    setPlaybackOptions(stream, {
                        mute: this.get('mute'),
                        skin: this.get('skin'),
                        autoplay: this.get('autoplay'),
                        autopause: this.get('autopause'),
                        repeat: this.get('repeat'),
                    })(jwConfig),
                )
                .then(addYoubora(stream, youboraSdk.state === 'READY'))
                .then(setKey(this.get('vendor')))
                .then((jwConfig) =>
                    utils.merge(
                        Object.assign({}, this.jwDefaults, jwConfig),
                        settings.jw || {},
                    ),
                )
        );
    }

    /**
     * Check if recommended is available
     */
    hasRecommended() {
        // recommended truned on and repeat truned off
        return (
            this.get('recommended') !== false && this.get('repeat') === false
        );
    }

    getRecommended() {
        return this.get('recommended');
    }

    getAgeLimit() {
        return this.get('ageLimit');
    }

    hasNext() {
        if (!this.hasRecommended()) {
            return false;
        }

        const recommended = this.getRecommended();
        if (
            recommended &&
            typeof recommended !== 'boolean' &&
            (recommended.next === false ||
                typeof recommended.next === 'undefined')
        ) {
            return false;
        }

        return true;
    }

    getPlaylistSettings() {
        return {
            ...this.getSettings(),
            adn: this.get('adn'),
            hasNext: this.hasNext(),
        };
    }

    getLiveMidrollTag(time: number): Promise<string | undefined> {
        return appnexus.getLiveMidrollTag(
            time,
            this.stream,
            this.getPlaylistSettings(),
        );
    }

    parse(options: ConfigModel['attributes']) {
        const asset = options.asset || options.id;
        const about = config.about[options.vendor as keyof typeof config.about];
        const skin = Object.assign(
            {},
            config.skins[options.vendor as keyof typeof config.skins] ||
                config.skins.default,
        );

        // set default skin for player if nothing is provided
        if (typeof options.skin === 'object') {
            if (options.skin.name) {
                skin.name += ` ${options.skin.name}`;
            }

            if (options.skin.url) {
                skin.url = options.skin.url;
            }
        } else {
            // remove if not valid
            delete options.skin;
        }

        options.skin = skin;

        // override about link/text
        if (!options.about && about) {
            options.about = about;
        }

        options.floating = {
            dismissible: options.floating?.dismissible ?? true,
            mode: options.floating?.mode ?? 'never',
        };

        // delete asset
        if (asset) {
            delete options.asset;
        }

        // check if user passed chapter or time
        const inputChapter = options.chapter as number | string | undefined;
        if (typeof inputChapter === 'string' && inputChapter.match(/^\d+$/)) {
            options.chapter = parseInt(inputChapter, 10);
        }

        // legacy support
        if (options.time) {
            options.time = utils.time.shareTimeToSeconds(
                options.time,
            ) as unknown as string;
        }

        if (options.settings) {
            delete options.settings;
        }

        return options;
    }
}

Object.defineProperty(ConfigModel.prototype, 'initialize', {
    value(options: PlainConfig) {
        const { vendor } = options;
        const rawOptions = { ...options };

        this.rawOptions = rawOptions;

        Domain.getPermissions(vendor).then((permissions) => {
            const settings: PlainConfig['settings'] = {};

            if (permissions.whitelist === true && options.settings) {
                Object.assign(settings, options.settings);
            } else if (
                permissions.whitelist === false &&
                options.settings &&
                options.settings.jw
            ) {
                settings.jw = { ...options.settings.jw };
            }

            if (permissions.preview === false) {
                delete settings.preview;
            }

            // turn off ads for certain provider
            if (config.ads[vendor] === false) {
                settings.na = true;
            }

            Object.defineProperty(this, 'settings', {
                value: Object.freeze(settings),
                writable: false,
            });

            Object.assign(this.attributes, this.parse(options));
            this.trigger('ready', rawOptions, settings);
        });
    },
    writable: false,
});

// Immutable method for restricted settings like preview or na
Object.defineProperty(ConfigModel.prototype, 'getSettings', {
    value() {
        return this.settings;
    },
    writable: false,
});

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface ConfigModel {
    initialize: (options: PlainConfig) => void;
    getSettings: () => Exclude<PlainConfig['settings'], undefined>;
}

export default ConfigModel;
